import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Clock, 
  User, 
  Calendar,
  X,
  Check
} from 'lucide-react';
import { appointmentTypesApi, workersApi, schedulesApi } from '../../services/api';
import Button from '../common/Button';
import Input from '../common/Input';
import './AppointmentSettings.css';

const DAYS_OF_WEEK = [
  { value: 0, labelKey: 'monday' },
  { value: 1, labelKey: 'tuesday' },
  { value: 2, labelKey: 'wednesday' },
  { value: 3, labelKey: 'thursday' },
  { value: 4, labelKey: 'friday' },
  { value: 5, labelKey: 'saturday' },
  { value: 6, labelKey: 'sunday' },
];

const AppointmentSettings = ({
  appointmentTypes,
  workers,
  schedules,
  onRefreshTypes,
  onRefreshWorkers,
  onRefreshSchedules,
  onClose,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('types');
  
  // Edit states
  const [editingType, setEditingType] = useState(null);
  const [editingWorker, setEditingWorker] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  // New item states
  const [newType, setNewType] = useState({ 
    name: '', 
    duration_minutes: 30, 
    price: '',
    description: '',
    prompt_notes: ''
  });
  const [newWorker, setNewWorker] = useState({ name: '' });
  const [newSchedule, setNewSchedule] = useState({ 
    day_of_week: 0, 
    start_time: '09:00', 
    end_time: '17:00',
    worker_id: ''
  });
  
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(false);

  // ============ Appointment Types ============
  const handleCreateType = async () => {
    if (!newType.name.trim()) {
      toast.error(t('settings.nameRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await appointmentTypesApi.create({
        name: newType.name,
        duration_minutes: parseInt(newType.duration_minutes),
        price: newType.price || null,
        description: newType.description || null,
        prompt_notes: newType.prompt_notes || null,
      });
      if (result.success) {
        toast.success(t('settings.typeCreated'));
        setNewType({ name: '', duration_minutes: 30, price: '', description: '', prompt_notes: '' });
        setShowNewForm(false);
        onRefreshTypes();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateType = async (type) => {
    setLoading(true);
    try {
      const result = await appointmentTypesApi.update(type.id, {
        name: editingType.name,
        duration_minutes: parseInt(editingType.duration_minutes),
        price: editingType.price || null,
        description: editingType.description || null,
        prompt_notes: editingType.prompt_notes || null,
      });
      if (result.success) {
        toast.success(t('settings.typeUpdated'));
        setEditingType(null);
        onRefreshTypes();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!window.confirm(t('settings.confirmDeleteType'))) return;
    
    setLoading(true);
    try {
      const result = await appointmentTypesApi.delete(typeId);
      if (result.success) {
        toast.success(t('settings.typeDeleted'));
        onRefreshTypes();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // ============ Workers ============
  const handleCreateWorker = async () => {
    if (!newWorker.name.trim()) {
      toast.error(t('settings.nameRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await workersApi.create(newWorker.name);
      if (result.success) {
        toast.success(t('settings.workerCreated'));
        setNewWorker({ name: '' });
        setShowNewForm(false);
        onRefreshWorkers();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateWorker = async (worker) => {
    setLoading(true);
    try {
      const result = await workersApi.update(worker.id, {
        name: editingWorker.name,
        is_active: editingWorker.is_active,
      });
      if (result.success) {
        toast.success(t('settings.workerUpdated'));
        setEditingWorker(null);
        onRefreshWorkers();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorker = async (workerId) => {
    if (!window.confirm(t('settings.confirmDeleteWorker'))) return;
    
    setLoading(true);
    try {
      const result = await workersApi.delete(workerId);
      if (result.success) {
        toast.success(t('settings.workerDeleted'));
        onRefreshWorkers();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  // ============ Schedules ============
  const handleCreateSchedule = async () => {
    if (!newSchedule.start_time || !newSchedule.end_time) {
      toast.error(t('settings.timeRequired'));
      return;
    }
    
    setLoading(true);
    try {
      const result = await schedulesApi.create({
        day_of_week: parseInt(newSchedule.day_of_week),
        start_time: newSchedule.start_time,
        end_time: newSchedule.end_time,
        worker_id: newSchedule.worker_id ? parseInt(newSchedule.worker_id) : null,
      });
      if (result.success) {
        toast.success(t('settings.scheduleCreated'));
        setNewSchedule({ day_of_week: 0, start_time: '09:00', end_time: '17:00', worker_id: '' });
        setShowNewForm(false);
        onRefreshSchedules();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSchedule = async (schedule) => {
    setLoading(true);
    try {
      const result = await schedulesApi.update(schedule.id, {
        day_of_week: parseInt(editingSchedule.day_of_week),
        start_time: editingSchedule.start_time,
        end_time: editingSchedule.end_time,
        is_active: editingSchedule.is_active,
      });
      if (result.success) {
        toast.success(t('settings.scheduleUpdated'));
        setEditingSchedule(null);
        onRefreshSchedules();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm(t('settings.confirmDeleteSchedule'))) return;
    
    setLoading(true);
    try {
      const result = await schedulesApi.delete(scheduleId);
      if (result.success) {
        toast.success(t('settings.scheduleDeleted'));
        onRefreshSchedules();
      } else {
        toast.error(result.error || t('common.error'));
      }
    } catch (error) {
      toast.error(error.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const getDayName = (dayValue) => {
    const day = DAYS_OF_WEEK.find(d => d.value === dayValue);
    return day ? t(`days.${day.labelKey}`) : '';
  };

  return (
    <div className="appointment-settings">
      <div className="settings-header">
        <h2>{t('appointments.settings')}</h2>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Tabs */}
      <div className="settings-tabs">
        <button 
          className={`tab-btn ${activeTab === 'types' ? 'active' : ''}`}
          onClick={() => { setActiveTab('types'); setShowNewForm(false); }}
        >
          <Clock size={16} />
          {t('settings.appointmentTypes')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'workers' ? 'active' : ''}`}
          onClick={() => { setActiveTab('workers'); setShowNewForm(false); }}
        >
          <User size={16} />
          {t('settings.workers')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'schedules' ? 'active' : ''}`}
          onClick={() => { setActiveTab('schedules'); setShowNewForm(false); }}
        >
          <Calendar size={16} />
          {t('settings.schedules')}
        </button>
      </div>

      {/* Content */}
      <div className="settings-content">
        {/* ============ Appointment Types Tab ============ */}
        {activeTab === 'types' && (
          <div className="settings-section">
            <div className="section-header">
              <h3>{t('settings.appointmentTypes')}</h3>
              <Button 
                variant="primary" 
                size="sm"
                icon={<Plus size={16} />}
                onClick={() => setShowNewForm(true)}
              >
                {t('common.add')}
              </Button>
            </div>

            {/* New Type Form */}
            {showNewForm && (
              <div className="new-item-form card">
                <div className="form-row">
                  <Input
                    label={t('common.name')}
                    value={newType.name}
                    onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                    placeholder={t('settings.typeName')}
                  />
                  <Input
                    label={t('settings.duration')}
                    type="number"
                    value={newType.duration_minutes}
                    onChange={(e) => setNewType({ ...newType, duration_minutes: e.target.value })}
                    min={5}
                    max={480}
                  />
                </div>
                <div className="form-row">
                  <Input
                    label={t('settings.price')}
                    type="number"
                    value={newType.price}
                    onChange={(e) => setNewType({ ...newType, price: e.target.value })}
                    placeholder={t('settings.priceOptional')}
                    step="0.01"
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">{t('settings.description')}</label>
                  <textarea
                    className="input textarea"
                    value={newType.description}
                    onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                    placeholder={t('settings.descriptionPlaceholder')}
                    rows={2}
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">{t('settings.promptNotes')}</label>
                  <textarea
                    className="input textarea"
                    value={newType.prompt_notes}
                    onChange={(e) => setNewType({ ...newType, prompt_notes: e.target.value })}
                    placeholder={t('settings.promptNotesPlaceholder')}
                    rows={2}
                  />
                </div>
                <div className="form-actions">
                  <Button variant="ghost" onClick={() => setShowNewForm(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="primary" onClick={handleCreateType} loading={loading}>
                    {t('common.create')}
                  </Button>
                </div>
              </div>
            )}

            {/* Types List */}
            <div className="items-list">
              {appointmentTypes.length === 0 ? (
                <p className="empty-text">{t('settings.noTypes')}</p>
              ) : (
                appointmentTypes.map((type) => (
                  <div key={type.id} className={`list-item card ${editingType?.id === type.id ? 'editing' : ''}`}>
                    {editingType?.id === type.id ? (
                      <div className="edit-form-expanded">
                        <div className="form-row">
                          <Input
                            label={t('common.name')}
                            value={editingType.name}
                            onChange={(e) => setEditingType({ ...editingType, name: e.target.value })}
                          />
                          <Input
                            label={t('settings.duration')}
                            type="number"
                            value={editingType.duration_minutes}
                            onChange={(e) => setEditingType({ ...editingType, duration_minutes: e.target.value })}
                            min={5}
                            max={480}
                          />
                          <Input
                            label={t('settings.price')}
                            type="number"
                            value={editingType.price || ''}
                            onChange={(e) => setEditingType({ ...editingType, price: e.target.value })}
                            step="0.01"
                          />
                        </div>
                        <div className="input-group">
                          <label className="input-label">{t('settings.description')}</label>
                          <textarea
                            className="input textarea"
                            value={editingType.description || ''}
                            onChange={(e) => setEditingType({ ...editingType, description: e.target.value })}
                            rows={2}
                          />
                        </div>
                        <div className="input-group">
                          <label className="input-label">{t('settings.promptNotes')}</label>
                          <textarea
                            className="input textarea"
                            value={editingType.prompt_notes || ''}
                            onChange={(e) => setEditingType({ ...editingType, prompt_notes: e.target.value })}
                            rows={2}
                          />
                        </div>
                        <div className="edit-actions-row">
                          <Button variant="ghost" size="sm" onClick={() => setEditingType(null)}>
                            {t('common.cancel')}
                          </Button>
                          <Button variant="primary" size="sm" onClick={() => handleUpdateType(type)} loading={loading}>
                            {t('common.save')}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="item-info">
                          <span className="item-name">{type.name}</span>
                          <span className="item-meta">{type.duration_minutes} {t('common.minutes')}</span>
                          {type.price && (
                            <span className="item-badge">₪{type.price}</span>
                          )}
                          {type.description && (
                            <span className="item-description">{type.description}</span>
                          )}
                        </div>
                        <div className="item-actions">
                          <button 
                            className="icon-btn" 
                            onClick={() => setEditingType({ ...type })}
                          >
                            <Pencil size={16} />
                          </button>
                          <button 
                            className="icon-btn danger" 
                            onClick={() => handleDeleteType(type.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ============ Workers Tab ============ */}
        {activeTab === 'workers' && (
          <div className="settings-section">
            <div className="section-header">
              <h3>{t('settings.workers')}</h3>
              <Button 
                variant="primary" 
                size="sm"
                icon={<Plus size={16} />}
                onClick={() => setShowNewForm(true)}
              >
                {t('common.add')}
              </Button>
            </div>

            {/* New Worker Form */}
            {showNewForm && (
              <div className="new-item-form card">
                <Input
                  label={t('common.name')}
                  value={newWorker.name}
                  onChange={(e) => setNewWorker({ ...newWorker, name: e.target.value })}
                  placeholder={t('settings.workerName')}
                />
                <div className="form-actions">
                  <Button variant="ghost" onClick={() => setShowNewForm(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="primary" onClick={handleCreateWorker} loading={loading}>
                    {t('common.create')}
                  </Button>
                </div>
              </div>
            )}

            {/* Workers List */}
            <div className="items-list">
              {workers.length === 0 ? (
                <p className="empty-text">{t('settings.noWorkers')}</p>
              ) : (
                workers.map((worker) => (
                  <div key={worker.id} className={`list-item card ${!worker.is_active ? 'inactive' : ''}`}>
                    {editingWorker?.id === worker.id ? (
                      <div className="edit-form">
                        <Input
                          value={editingWorker.name}
                          onChange={(e) => setEditingWorker({ ...editingWorker, name: e.target.value })}
                        />
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={editingWorker.is_active}
                            onChange={(e) => setEditingWorker({ ...editingWorker, is_active: e.target.checked })}
                          />
                          {t('common.active')}
                        </label>
                        <div className="edit-actions">
                          <button className="icon-btn success" onClick={() => handleUpdateWorker(worker)}>
                            <Check size={16} />
                          </button>
                          <button className="icon-btn" onClick={() => setEditingWorker(null)}>
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="item-info">
                          <span className="item-name">{worker.name}</span>
                          {!worker.is_active && (
                            <span className="item-badge inactive">{t('common.inactive')}</span>
                          )}
                        </div>
                        <div className="item-actions">
                          <button 
                            className="icon-btn" 
                            onClick={() => setEditingWorker({ ...worker })}
                          >
                            <Pencil size={16} />
                          </button>
                          <button 
                            className="icon-btn danger" 
                            onClick={() => handleDeleteWorker(worker.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ============ Schedules Tab ============ */}
        {activeTab === 'schedules' && (
          <div className="settings-section">
            <div className="section-header">
              <h3>{t('settings.schedules')}</h3>
              <Button 
                variant="primary" 
                size="sm"
                icon={<Plus size={16} />}
                onClick={() => setShowNewForm(true)}
              >
                {t('common.add')}
              </Button>
            </div>

            {/* New Schedule Form */}
            {showNewForm && (
              <div className="new-item-form card">
                <div className="form-row">
                  <div className="input-group">
                    <label className="input-label">{t('settings.dayOfWeek')}</label>
                    <select
                      className="input"
                      value={newSchedule.day_of_week}
                      onChange={(e) => setNewSchedule({ ...newSchedule, day_of_week: e.target.value })}
                    >
                      {DAYS_OF_WEEK.map((day) => (
                        <option key={day.value} value={day.value}>
                          {t(`days.${day.labelKey}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="input-group">
                    <label className="input-label">{t('settings.worker')}</label>
                    <select
                      className="input"
                      value={newSchedule.worker_id}
                      onChange={(e) => setNewSchedule({ ...newSchedule, worker_id: e.target.value })}
                    >
                      <option value="">{t('settings.allWorkers')}</option>
                      {workers.filter(w => w.is_active).map((worker) => (
                        <option key={worker.id} value={worker.id}>
                          {worker.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="form-row">
                  <Input
                    label={t('settings.startTime')}
                    type="time"
                    value={newSchedule.start_time}
                    onChange={(e) => setNewSchedule({ ...newSchedule, start_time: e.target.value })}
                  />
                  <Input
                    label={t('settings.endTime')}
                    type="time"
                    value={newSchedule.end_time}
                    onChange={(e) => setNewSchedule({ ...newSchedule, end_time: e.target.value })}
                  />
                </div>
                <div className="form-actions">
                  <Button variant="ghost" onClick={() => setShowNewForm(false)}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="primary" onClick={handleCreateSchedule} loading={loading}>
                    {t('common.create')}
                  </Button>
                </div>
              </div>
            )}

            {/* Schedules List */}
            <div className="items-list">
              {schedules.length === 0 ? (
                <p className="empty-text">{t('settings.noSchedules')}</p>
              ) : (
                schedules.map((schedule) => (
                  <div key={schedule.id} className={`list-item card ${!schedule.is_active ? 'inactive' : ''}`}>
                    {editingSchedule?.id === schedule.id ? (
                      <div className="edit-form schedule-edit">
                        <select
                          className="input"
                          value={editingSchedule.day_of_week}
                          onChange={(e) => setEditingSchedule({ ...editingSchedule, day_of_week: e.target.value })}
                        >
                          {DAYS_OF_WEEK.map((day) => (
                            <option key={day.value} value={day.value}>
                              {t(`days.${day.labelKey}`)}
                            </option>
                          ))}
                        </select>
                        <input
                          type="time"
                          className="input"
                          value={editingSchedule.start_time}
                          onChange={(e) => setEditingSchedule({ ...editingSchedule, start_time: e.target.value })}
                        />
                        <input
                          type="time"
                          className="input"
                          value={editingSchedule.end_time}
                          onChange={(e) => setEditingSchedule({ ...editingSchedule, end_time: e.target.value })}
                        />
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={editingSchedule.is_active}
                            onChange={(e) => setEditingSchedule({ ...editingSchedule, is_active: e.target.checked })}
                          />
                          {t('common.active')}
                        </label>
                        <div className="edit-actions">
                          <button className="icon-btn success" onClick={() => handleUpdateSchedule(schedule)}>
                            <Check size={16} />
                          </button>
                          <button className="icon-btn" onClick={() => setEditingSchedule(null)}>
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="item-info">
                          <span className="item-name">{getDayName(schedule.day_of_week)}</span>
                          <span className="item-meta">
                            {schedule.start_time} - {schedule.end_time}
                          </span>
                          {schedule.worker_name && (
                            <span className="item-badge">{schedule.worker_name}</span>
                          )}
                          {!schedule.is_active && (
                            <span className="item-badge inactive">{t('common.inactive')}</span>
                          )}
                        </div>
                        <div className="item-actions">
                          <button 
                            className="icon-btn" 
                            onClick={() => setEditingSchedule({ 
                              ...schedule,
                              start_time: schedule.start_time?.slice(0, 5) || '09:00',
                              end_time: schedule.end_time?.slice(0, 5) || '17:00',
                            })}
                          >
                            <Pencil size={16} />
                          </button>
                          <button 
                            className="icon-btn danger" 
                            onClick={() => handleDeleteSchedule(schedule.id)}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AppointmentSettings;

